import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import SelectEntityAsync from "components/SelectEntityAsync";
import arrayMutators from "final-form-arrays";
import React, { useCallback, useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  SaveResourceLibraryItemAttributeRequest,
  IResourceLibraryItem,
  AttributeFilter,
  ListRequestOfAttributeFilter,
  ListResponseOfAttribute,
  OrganizationAttributeTypeRequest,
} from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import { AttributeTypes } from "types/common-types";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
}));

const { Form } = withTypes<SaveResourceLibraryItemAttributeRequest>();

type ResourceLibraryItemTopicFormProps = {
  resourceLibraryItem: IResourceLibraryItem;
  submitLabel: string;
  onSubmit: (values: SaveResourceLibraryItemAttributeRequest) => void;
  onCancel?: () => void;
};

const ResourceLibraryItemTopicForm = ({
  resourceLibraryItem,
  submitLabel,
  onCancel,
  onSubmit,
}: ResourceLibraryItemTopicFormProps &
  FormProps<SaveResourceLibraryItemAttributeRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const queryClient = useQueryClient();
  const { attributeService, resourceLibraryService } = useGlobalContext();

  const resourceLibraryQuery = useQuery(
    ["resourceLibraryService.getResourceLibraryById", resourceLibraryItem.resourceLibraryId],
    () =>
      resourceLibraryService.getResourceLibraryById(
        resourceLibraryItem.resourceLibraryId!,
      ),
    { enabled: !!resourceLibraryItem.resourceLibraryId },
  );

  const organizationId = resourceLibraryQuery.data?.data?.organizationId;

  const oatQuery = useQuery(
    ["attributeService.getOrganizationAttributeTypes", organizationId],
    () =>
      attributeService.getOrganizationAttributeTypes(
        OrganizationAttributeTypeRequest.fromJS({
          organizationId: organizationId,
          parameters: {
            codeConstant: AttributeTypes.Topics,
          },
        }),
      ),
    { enabled: !!organizationId },
  );

  const topicsOrganizationAttributeType = oatQuery.data?.data?.[0];

  const fetchAttributes = useCallback(
    async (searchTerm: string) => {
      let result = new ListResponseOfAttribute();
      if (topicsOrganizationAttributeType?.id) {
        result = await queryClient.fetchQuery<ListResponseOfAttribute>(
          ["attributeService.getAttributes", topicsOrganizationAttributeType.id, searchTerm],
          () =>
            attributeService.getAttributes(
              new ListRequestOfAttributeFilter({
                pageNumber: 0,
                pageSize: 20,
                parameters: AttributeFilter.fromJS({
                  searchText: searchTerm === "" ? undefined : searchTerm,
                  organizationAttributeTypeId: topicsOrganizationAttributeType.id!,
                }),
                sortField: "value",
              }),
            ),
        );
      }

      return { result, forSearchTerm: searchTerm };
    },
    [attributeService, queryClient, topicsOrganizationAttributeType],
  );

  const isLoading = resourceLibraryQuery.isLoading || oatQuery.isLoading;

  return useMemo(
    () => (
      <Form
        onSubmit={onSubmit}
        mutators={{ ...arrayMutators }}
        render={({ handleSubmit, submitting, submitError, values }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  {isLoading ? (
                    <Grid item xs={12}>
                      <Typography>
                        <T id="loading" />
                      </Typography>
                    </Grid>
                  ) : !topicsOrganizationAttributeType ? (
                    <Grid item xs={12}>
                      <Alert severity="warning">
                        <T id="resourceLibraryNoTopicsOATConfigured" />
                      </Alert>
                    </Grid>
                  ) : (
                    <Field name="attributeId" required>
                      {({ input, meta }) => (
                        <Grid item xs={12}>
                          <FormControl
                            fullWidth
                            error={meta.error && meta.submitFailed}
                          >
                            <SelectEntityAsync
                              fetchData={fetchAttributes}
                              value={input.value}
                              onChange={input.onChange}
                              label={t({
                                id: "resourceLibraryFormAttributeLabel",
                              })}
                              labelField={"value"}
                              failMessage={t({ id: "fetchError" })}
                            />
                            {meta.error && meta.submitFailed && (
                              <FormHelperText>{meta.error}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      )}
                    </Field>
                  )}

                  {submitError && (
                    <Alert sx={styles.feedback} severity="error">
                      {submitError}
                    </Alert>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={submitting} onClick={onCancel}>
                  {t({ id: "cancelButton" })}
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  disabled={submitting || !values.attributeId || !topicsOrganizationAttributeType}
                >
                  {submitLabel}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    ),
    [
      styles.feedback,
      onCancel,
      onSubmit,
      submitLabel,
      t,
      isLoading,
      topicsOrganizationAttributeType,
      fetchAttributes,
    ],
  );
};

export default ResourceLibraryItemTopicForm;
